import React, { FC, useContext, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import {
  PaymentPlanDetailItem,
  PaymentPlanInstallmentStatus,
} from "@app/postBill/models/apiControllerModel";
import { convertBalance } from "@app/postBill/utils/convertBalance";
import { getOrdinalSuffix } from "@app/postBill/utils";
import { PostBillAPIContext } from "@app/provider";
import { logMsg } from "@app/utils/logMsg";

import "./../paymentSummaryCard/paymentSummaryCard.scss";

const messages = defineMessages({
  paymentSummaryCardPayFullBalanceSinglePaymentLabel: {
    id: "paymentSummaryCardPayFullBalanceSinglePaymentLabel",
    defaultMessage: "{startIndex} payment",
  },
  paymentSummaryCardPayFullBalancePaymentLabel: {
    id: "paymentSummaryCardPayFullBalancePaymentLabel",
    defaultMessage: "{startIndex} - {endIndex} payments",
  },
  paymentSummaryCardTotalTestsLabel: {
    id: "paymentSummaryCardTotalTestsLabel",
    defaultMessage: "Total ({count, plural, one {# test} other {# tests}})",
  },
});

interface PaymentFullSummaryCardProps {
  paymentPlanId: string;
}
interface InstallmentAmountByIndexes {
  amount: number;
  indexes: number[];
}

const PaymentFullSummaryCard: FC<PaymentFullSummaryCardProps> = ({
  paymentPlanId,
}) => {
  const { getPaymentPlanDetail } = useContext(PostBillAPIContext);
  const intl = useIntl();
  const [installmentAmountByIndexesList, setInstallmentAmountByIndexesList] =
    useState<InstallmentAmountByIndexes[]>();
  const [remainingTotalBalance, setRemainingTotalBalance] = useState<number>(0);
  const [currency, setCurrency] = useState<string>("");
  const [testCount, setTestCount] = useState<number>(0);

  const generateInstallmentsList = (paymentDetail: PaymentPlanDetailItem) => {
    const installmentAmountByIndexes: InstallmentAmountByIndexes[] = [];
    paymentDetail.installments.forEach((installment) => {
      const calculatedIndex = installmentAmountByIndexes.findIndex(
        (element) => element.amount === installment.amount
      );

      if (installment.status !== PaymentPlanInstallmentStatus.PAID) {
        if (calculatedIndex === -1) {
          installmentAmountByIndexes.push({
            amount: installment.amount,
            indexes: [installment.index],
          });
        } else {
          installmentAmountByIndexes[calculatedIndex].indexes.push(
            installment.index
          );
        }
      }
    });
    setInstallmentAmountByIndexesList(installmentAmountByIndexes);
  };

  const getPaymentPlanDetails = async (paymentPlanId: string) => {
    try {
      const getPaymentPlanDetails = await getPaymentPlanDetail({
        paymentPlanId,
      });
      if (getPaymentPlanDetails.data) {
        setRemainingTotalBalance(getPaymentPlanDetails.data.remainingBalance);
        setCurrency(getPaymentPlanDetails.data.currency);
        setTestCount(getPaymentPlanDetails.data.testName.split(",").length);
        generateInstallmentsList(getPaymentPlanDetails.data);
      }
    } catch {
      logMsg(
        "error",
        "postbill.payment-plan",
        "Failed to get Payment Plan Details"
      );
    }
  };

  const renderPaymentLabels = (
    installmentAmountByIndexesList: InstallmentAmountByIndexes[]
  ) => {
    return installmentAmountByIndexesList.map((installmentAmountByIndex) => {
      const indexesLength = installmentAmountByIndex.indexes.length;
      const startIndex = getOrdinalSuffix(
        installmentAmountByIndex.indexes[0],
        intl
      );
      const hasEndIndex = indexesLength > 1;
      if (hasEndIndex) {
        const endIndex = getOrdinalSuffix(
          installmentAmountByIndex.indexes[indexesLength - 1],
          intl
        );
        return (
          <div
            key={installmentAmountByIndex.amount}
            className="payment-summary-card__row"
          >
            <span>
              {intl.formatMessage(
                messages.paymentSummaryCardPayFullBalancePaymentLabel,
                {
                  startIndex,
                  endIndex,
                }
              )}
            </span>
            <span className="payment-summary-card__value">
              {convertBalance(installmentAmountByIndex.amount, true, currency) +
                " x " +
                installmentAmountByIndex.indexes.length}
            </span>
          </div>
        );
      }
      return (
        <div
          key={installmentAmountByIndex.amount}
          className="payment-summary-card__row"
        >
          <span>
            {intl.formatMessage(
              messages.paymentSummaryCardPayFullBalanceSinglePaymentLabel,
              {
                startIndex,
              }
            )}
          </span>
          <span className="payment-summary-card__value">
            {convertBalance(installmentAmountByIndex.amount, true, currency)}
          </span>
        </div>
      );
    });
  };

  useEffect(() => {
    if (paymentPlanId) {
      getPaymentPlanDetails(paymentPlanId);
    }
  }, []);

  return (
    <>
      {installmentAmountByIndexesList &&
        installmentAmountByIndexesList.length > 0 && (
          <div className="payment-summary-card">
            {installmentAmountByIndexesList &&
              renderPaymentLabels(installmentAmountByIndexesList)}
            <div className="payment-summary-card__row payment-summary-card__total">
              <span>
                {intl.formatMessage(
                  messages.paymentSummaryCardTotalTestsLabel,
                  {
                    count: testCount,
                  }
                )}
              </span>
              <span className="payment-summary-card__value">
                {convertBalance(remainingTotalBalance, true, currency)}
              </span>
            </div>
          </div>
        )}
    </>
  );
};

export default PaymentFullSummaryCard;
